import React from 'react';
import { Box, Card, CardContent, CardHeader, Switch, Typography } from '@material-ui/core';
import {
    formatDiscountPreview,
    getStrategyLabel,
    PRICE_STRATEGIES,
} from '../../../constants/promotionConfig';
import {
    TIER_DISCOUNT_TYPES,
    TIER_SCOPE_LABELS,
    tiersToThresholds,
} from '../../../constants/pricingTierConfig';

const sidebarCardStyle = (isMobile) =>
    isMobile
        ? {
              marginBottom: 12,
              borderRadius: 12,
              border: '1px solid #E8E8E8',
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.06)',
          }
        : { marginBottom: 16 };

const SummaryRow = ({ label, value }) => (
    <Box mb={2}>
        <Typography variant="caption" color="textSecondary" display="block" style={{ fontWeight: 600 }}>
            {label}
        </Typography>
        <Typography variant="body2" style={{ marginTop: 2 }}>
            {value || '—'}
        </Typography>
    </Box>
);

export const PromotionStatusPanel = ({ active, onActiveChange, isMobile, inline }) => {
    if (inline) {
        return (
            <Box display="flex" alignItems="center">
                <Switch
                    color="primary"
                    checked={active}
                    onChange={(e) => onActiveChange(e.target.checked)}
                    size="small"
                />
                <Typography variant="caption" style={{ color: active ? '#2E7D32' : '#999', fontSize: 12 }}>
                    {active ? 'Activa' : 'Inactiva'}
                </Typography>
            </Box>
        );
    }

    return (
        <Card elevation={isMobile ? 0 : 2} style={sidebarCardStyle(isMobile)}>
            <CardHeader
                titleTypographyProps={{ variant: isMobile ? 'subtitle1' : 'h6' }}
                title="Estado de la promoción"
            />
            <CardContent style={isMobile ? { padding: '8px 16px 16px' } : { paddingTop: 0 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Promoción activa</Typography>
                    <Switch
                        color="primary"
                        checked={active}
                        onChange={(e) => onActiveChange(e.target.checked)}
                    />
                </Box>
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                    {active
                        ? 'La promoción puede aplicar descuentos según su vigencia y alcance.'
                        : 'La promoción queda guardada pero inactiva en la tienda.'}
                </Typography>
            </CardContent>
        </Card>
    );
};

export const PromotionSummaryPanel = ({ form, storeCurrency, isEdit, loadedProductCount, isMobile }) => {
    const isQuantityRule = form.rule_type === 'quantity_discount';

    const scheduleText = form.useSchedule
        ? form.starts_at && form.ends_at
            ? `Del ${form.starts_at} al ${form.ends_at} (solo en ese periodo)`
            : 'Completa las fechas de inicio y fin'
        : isEdit
          ? 'Sin fechas: aplica mientras esté activa'
          : 'Sin fechas: aplica al guardar mientras esté activa';

    let targetingText = '—';
    const scopeKey = isQuantityRule ? form.scope : form.targetType;

    if (isQuantityRule && scopeKey === 'store') {
        targetingText = TIER_SCOPE_LABELS.store;
    } else if (scopeKey === 'product') {
        const n = form.selectedProducts?.length ?? 0;
        targetingText =
            n === 0
                ? isEdit && loadedProductCount > 0
                    ? `${loadedProductCount} producto(s) actuales`
                    : 'Ningún producto'
                : `${n} producto(s)`;
    } else if (scopeKey === 'collection') {
        const n = form.selectedCollections?.length ?? 0;
        targetingText =
            n === 0
                ? isEdit && loadedProductCount > 0
                    ? `${loadedProductCount} producto(s) actuales`
                    : 'Ninguna categoría'
                : `${n} categoría(s)`;
    } else if (form.targetType === 'product') {
        const n = form.selectedProducts?.length ?? 0;
        targetingText =
            n === 0
                ? isEdit && loadedProductCount > 0
                    ? `${loadedProductCount} producto(s) actuales (sin cambios en selección)`
                    : 'Ningún producto seleccionado'
                : `${n} producto(s) seleccionado(s)`;
    } else {
        const n = form.selectedCollections?.length ?? 0;
        targetingText =
            n === 0
                ? isEdit && loadedProductCount > 0
                    ? `${loadedProductCount} producto(s) actuales (sin cambios en selección)`
                    : 'Ninguna categoría seleccionada'
                : `${n} categoría(s) seleccionada(s)`;
    }

    const discountPreview = formatDiscountPreview(
        form.discount_type,
        form.discount_value,
        storeCurrency
    );

    const tierLevelCount = isQuantityRule ? tiersToThresholds(form.tiers || []).length : 0;
    const discountTypeLabel =
        TIER_DISCOUNT_TYPES.find((t) => t.id === form.discount_type)?.label || '—';

    return (
        <Card elevation={isMobile ? 0 : 2} style={sidebarCardStyle(isMobile)}>
            <CardHeader
                titleTypographyProps={{ variant: isMobile ? 'subtitle1' : 'h6' }}
                title={isQuantityRule ? 'Resumen de la regla' : 'Resumen del descuento'}
            />
            <CardContent style={isMobile ? { padding: '8px 16px 16px' } : { paddingTop: 0 }}>
                <SummaryRow label="Nombre" value={form.name?.trim() || '—'} />
                <SummaryRow label="Alcance" value={targetingText} />
                {isQuantityRule ? (
                    <>
                        <SummaryRow
                            label="Niveles"
                            value={
                                tierLevelCount > 0
                                    ? `${tierLevelCount} nivel${tierLevelCount > 1 ? 'es' : ''}`
                                    : '—'
                            }
                        />
                        <SummaryRow label="Tipo de descuento" value={discountTypeLabel} />
                    </>
                ) : (
                    <>
                        <SummaryRow label="Monto del descuento" value={discountPreview} />
                        <SummaryRow
                            label="Precio en tienda"
                            value={
                                form.price_strategy
                                    ? (() => {
                                          const s = PRICE_STRATEGIES.find(
                                              (x) => x.id === form.price_strategy
                                          );
                                          return s
                                              ? isMobile
                                                  ? s.label
                                                  : `${s.label} — ${s.shortHint}`
                                              : getStrategyLabel(form.price_strategy);
                                      })()
                                    : '—'
                            }
                        />
                    </>
                )}
                <SummaryRow label="Vigencia" value={scheduleText} />
                <SummaryRow label="Estado" value={form.active ? 'Activa' : 'Inactiva'} />
            </CardContent>
        </Card>
    );
};

const PromotionSidebar = (props) => (
    <Box>
        <PromotionSummaryPanel {...props} />
        {!props.isMobile && (
            <PromotionStatusPanel
                active={props.form.active}
                onActiveChange={props.onActiveChange}
                isMobile={false}
            />
        )}
    </Box>
);

export default PromotionSidebar;
